/**
 * `useRailSpaceGovernor` — the nav rail yields when panes compete for width.
 *
 * Operator rule (2026-07-21): whenever a space-competing surface is up, the
 * left nav bar collapses to its icon rail automatically; when they all go away,
 * the lawyer's own preference comes back. Stated two ways in the same session —
 * "when multiple panes are visible they fight for space, the leftside bar
 * should always be automatically minimized" and "when the Agents sidebar is on,
 * the leftside navbar should automatically collapse" — which is one rule, so it
 * is implemented once, here, rather than at each pane's open/close.
 *
 * Competing surfaces:
 *   - the FOCUS PANE (tasklist / browser / contacts),
 *   - the WORKSPACE FILE VIEWER,
 *   - the AGENTS right sidebar.
 *
 * The user's expanded/collapsed preference is captured on the FIRST transition
 * into a competing layout and released on the transition back out. Capturing it
 * once is what stops the collapsed state from being mistaken for the
 * preference: re-capturing while already collapsed would make "restore" restore
 * the collapse, permanently.
 *
 * The lawyer stays in charge. If they expand the rail BY HAND while a pane is
 * open — deliberately choosing to live with less room — that is newer intent
 * than the preference captured on the way in, so the release does not undo it.
 * Without that check the governor silently slams the rail back and the manual
 * expand looks like it "didn't take".
 */
import { useEffect, useRef } from "react";

import { useUIStore } from "@/stores/useUIStore";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";

export function useRailSpaceGovernor(): void {
  const focusPane = useUIStore((s) => s.focusPane);
  const rightSidebarExpanded = useUIStore((s) => s.rightSidebarExpanded);
  const setSidebarExpanded = useUIStore((s) => s.setSidebarExpanded);
  const activeFile = useWorkspaceFileStore((s) => s.activeFile);

  const competing =
    focusPane !== null || activeFile !== null || rightSidebarExpanded;

  /** The rail state to hand back once nothing is competing. */
  const savedRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (competing) {
      if (savedRef.current === null) {
        // Capture ONCE, on the way in.
        savedRef.current = useUIStore.getState().sidebarExpanded;
      }
      if (useUIStore.getState().sidebarExpanded) setSidebarExpanded(false);
      return;
    }
    if (savedRef.current !== null) {
      // Restore ONLY if the rail is still in the state we forced. If it is
      // expanded, the lawyer expanded it themselves while the pane was up and
      // that intent is newer than our snapshot.
      if (!useUIStore.getState().sidebarExpanded) {
        setSidebarExpanded(savedRef.current);
      }
      savedRef.current = null;
    }
  }, [competing, setSidebarExpanded]);
}
